import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { ApiClientService, DefaultTokenService } from '@dfl/react-security';
import { useLocalStorage } from 'hooks/useLocalStorage';

interface SocketContextValue {
  socket: Socket | null;
  isConnected: boolean;
  error: string | null;
  connect: () => void;
  disconnect: () => void;
}

const SocketContext = createContext<SocketContextValue | undefined>(undefined);

let globalSocketInstance: Socket | null = null;
let isInitializing = false;

interface SocketProviderProps {
  children: ReactNode;
  namespace?: string;
  path?: string;
  reconnection?: boolean;
  reconnectionAttempts?: number;
  reconnectionDelay?: number;
  autoConnect?: boolean;
}

export const SocketProvider = ({
  children,
  namespace = '/notifications',
  path = '/ms-auth/ws',
  reconnection = true,
  reconnectionAttempts = 10,
  reconnectionDelay = 3000,
  autoConnect = true,
}: SocketProviderProps) => {
  const socketRef = useRef<Socket | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isConnectingRef = useRef(false);
  const configRef = useRef({ namespace, path, reconnection, reconnectionAttempts, reconnectionDelay });
  const { storedValue: getCurrentToken } = useLocalStorage(
    process.env.REACT_APP_ACCESS_TOKKEN || '__access_token__',
    null,
  );

  // Update config ref when props change
  useEffect(() => {
    configRef.current = { namespace, path, reconnection, reconnectionAttempts, reconnectionDelay };
  }, [namespace, path, reconnection, reconnectionAttempts, reconnectionDelay]);

  const connect = useCallback(() => {
    // Prevent multiple simultaneous connection attempts using global and local flags
    if (isInitializing || isConnectingRef.current || globalSocketInstance?.connected) {
      if (globalSocketInstance && !socketRef.current) {
        socketRef.current = globalSocketInstance;
        setSocket(globalSocketInstance);
        setIsConnected(globalSocketInstance.connected);
      }
      return;
    }

    isInitializing = true;
    isConnectingRef.current = true;

    const {
      namespace: ns,
      path: p,
      reconnection: recon,
      reconnectionAttempts: attempts,
      reconnectionDelay: delay,
    } = configRef.current;
    const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';
    const token = ApiClientService.getToken();

    if (!token) {
      setError('No authentication token available');
      isConnectingRef.current = false;
      isInitializing = false;
      return;
    }

    // Disconnect existing socket if any
    if (globalSocketInstance) {
      globalSocketInstance.removeAllListeners();
      globalSocketInstance.disconnect();
      globalSocketInstance = null;
    }
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const socketInstance = io(`${baseUrl}${ns}`, {
      transports: ['websocket'],
      path: p,
      auth: {
        Authorization: `Bearer ${token}`,
        token: `Bearer ${token}`,
      },
      reconnection: recon,
      reconnectionAttempts: attempts,
      reconnectionDelay: delay,
    });

    socketInstance.on('connect', () => {
      setIsConnected(true);
      setError(null);
      socketInstance.emit('subscribe');
      isConnectingRef.current = false;
      isInitializing = false;
    });

    socketInstance.on('connect_error', (err) => {
      setError(err.message);
      setIsConnected(false);
      isConnectingRef.current = false;
      isInitializing = false;
    });

    socketInstance.on('disconnect', (reason) => {
      setIsConnected(false);
      isConnectingRef.current = false;
    });

    socketInstance.on('error', async (err) => {
      if (err.message === 'invalidToken') {
        setError('Authentication token expired');
        socketInstance.disconnect();
        isConnectingRef.current = false;
        isInitializing = false;
        try {
          await DefaultTokenService.refresh();
          connect();
        } catch (refreshError) {
          setError('Failed to refresh authentication token');
        }
      }
    });

    socketInstance.on('auth_expired', async () => {
      setError('Authentication token expired');
      socketInstance.disconnect();
      isConnectingRef.current = false;
      isInitializing = false;
      try {
        await DefaultTokenService.refresh();
        connect();
      } catch (refreshError) {
        setError('Failed to refresh authentication token');
      }
    });

    globalSocketInstance = socketInstance;
    socketRef.current = socketInstance;
    setSocket(socketInstance);
  }, []);

  const disconnect = useCallback(() => {
    isConnectingRef.current = false;
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
      setSocket(null);
      setIsConnected(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    if (autoConnect && !globalSocketInstance && !isInitializing && mounted) {
      connect();
    }

    return () => {
      mounted = false;
      isConnectingRef.current = false;
      isInitializing = false;

      if (globalSocketInstance) {
        globalSocketInstance.removeAllListeners();
        globalSocketInstance.disconnect();
        globalSocketInstance = null;
      }
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setSocket(null);
      setIsConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  const value: SocketContextValue = {
    socket,
    isConnected,
    error,
    connect,
    disconnect,
  };

  // Re-connect when refreshToken has been updated
  useEffect(() => {
    const token = ApiClientService.getToken();
    if (getCurrentToken && token && token !== getCurrentToken) {
      connect();
    }
  }, [connect, getCurrentToken]);

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};

export const useSocket = (): SocketContextValue => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};
